import { computed, ref, watch } from 'vue';
import { store } from '@/plugins/store';
import { useMortalityObjectiveHistory, useMortalityObjectivesOverview } from '@/plugins/store/actions/queries/objectives';
import { useExpandedIds } from '@/composables/useExpandedIds';
import ManageObjectivesModal from '@/components/common/objectives/ManageObjectivesModal.vue';
import { type FarmHistoryRow, type TrendDirection, findBarnTrendDirection, toFarmGroupRows, toFarmHistoryRow } from './objectiveRow';
import { getReferenceWindowOptions, readStoredReferenceWindow, writeStoredReferenceWindow } from './referenceWindow';
import { computeSharedCeiling } from './objectiveBar';
import { computeLeaderboardSplit, resolveLeaderboardDisabledReason, type LeaderboardDisabledReason } from './leaderboard';

// All page-level state and derivation for the Objectives table: reference-window selection and its
// persistence, the overview + history queries and their combined refetch, the Farm/Barn/Section row
// list, the history-driven trend lookup, the leaderboard and its gating, the shared mortality-bar
// ceiling, and the two independent expand/collapse sets (Barn rows, per-Farm trend charts).
// Index.vue wires the return value straight into its template and holds no derivation of its own.
export function useObjectivesPage() {
    const isWeaners = computed<boolean>(() => store.getters.getIsWeaners);
    const siteIds = computed<number[]>(() => store.getters.getSelectedSiteIdsFilteredByPigType);

    // Reference-window (weeks) selection: the option set depends on the global pig-type toggle (see
    // referenceWindow.ts), and the last-chosen value is persisted per animal type so flipping the
    // global toggle restores that type's own last choice (or its default, if it never had one)
    // rather than carrying the other type's pick over.
    const referenceWindowOptions = computed<number[]>(() => getReferenceWindowOptions(isWeaners.value));
    const referenceWindowWeeks = ref<number>(readStoredReferenceWindow(isWeaners.value));

    function selectReferenceWindow(weeks: number): void {
        referenceWindowWeeks.value = weeks;
        writeStoredReferenceWindow(isWeaners.value, weeks);
    }

    const { data, isLoading, isError, refetch, isFetching, isPlaceholderData } = useMortalityObjectivesOverview({ weeks: referenceWindowWeeks, isWeaners, siteIds });

    // `keepPreviousData` (see the query hook) keeps `status: 'success'` with the old animal-type's
    // rows as placeholder data while a pig-type/reference-window switch refetches, so `isLoading`
    // never goes true for that switch — without this, the table would keep rendering the old type's
    // rows with no visual cue for how long the round-trip is taking. `isPlaceholderData` scopes this
    // to a real placeholder-backed refetch, not every background refetch (e.g. a stale-time
    // revalidation of already-correct data, which shouldn't dim the table).
    const isRefetchingPlaceholder = computed<boolean>(() => isFetching.value && isPlaceholderData.value);

    const farmGroups = computed(() => toFarmGroupRows(data.value?.farms ?? []));
    const hasFarms = computed<boolean>(() => farmGroups.value.length > 0);

    // Trend-history sparkline side-tile: a separate query from the overview above, same
    // reference-window (weeks), fixed default period count. Failure is non-fatal to the page: on
    // error `historyData` just stays undefined and every Farm falls back to no sparkline tile at
    // all, same as "still loading" — the overview above is this page's primary content.
    const historyPeriods = ref<number>(8);
    const { data: historyData, refetch: refetchHistory } = useMortalityObjectiveHistory({ weeks: referenceWindowWeeks, periods: historyPeriods, isWeaners, siteIds });

    // Setting/clearing a target changes the resolved target both queries evaluate against —
    // refetching only the overview would leave the sparkline showing stale figures against the old
    // target. Both refetch together on every save.
    function refetchAll(): void {
        refetch();
        refetchHistory();
    }

    const historyByFarmId = computed<Map<number, FarmHistoryRow>>(() => {
        const map = new Map<number, FarmHistoryRow>();
        for (const farm of historyData.value?.farms ?? []) {
            map.set(farm.location_id, toFarmHistoryRow(farm));
        }
        return map;
    });

    // Improvement arrow (gamification): looked up per Barn from the history query above, since the
    // live overview query BarnRow otherwise renders from carries no history of its own. See
    // objectiveRow.ts's findBarnTrendDirection for the actual Farm/Barn id join.
    function trendDirectionFor(farmId: number, barnId: number): TrendDirection | null {
        return findBarnTrendDirection(historyByFarmId.value, farmId, barnId);
    }

    // Leaderboard (gamification): Best/Worst split, recomputed from the same overview data as
    // everything else on the page. See leaderboard.ts for the Barn-level-not-Farm-level and
    // zero-dead-excluded rules, the mutual-exclusivity/thresholding behind the split, and
    // TopPerformersModal.vue for why a figure with no target reads grey there rather than green.
    const leaderboard = computed(() => computeLeaderboardSplit(farmGroups.value));

    const totalBarnCount = computed<number>(() => farmGroups.value.reduce((sum, farm) => sum + farm.barns.length, 0));
    const leaderboardDisabledReason = computed<LeaderboardDisabledReason>(() => resolveLeaderboardDisabledReason(totalBarnCount.value, leaderboard.value.best.length));

    // One shared bar scale for every Barn and Section row in the table, computed from every target
    // currently loaded — not just the ones in expanded/visible Sections — so the scale doesn't jump
    // when a Barn is expanded or collapsed.
    const barCeiling = computed<number>(() =>
        computeSharedCeiling(farmGroups.value.flatMap((farm) => farm.barns.flatMap((barn) => [barn.resolvedTarget, ...barn.sections.map((section) => section.resolvedTarget)])))
    );

    // Expand/collapse state for expandable Barn rows — collapsed by default.
    const { isExpanded, toggle, reset: resetExpanded } = useExpandedIds();

    // Which Farms have their trend chart open — its own independent Set, collapsed by default,
    // several Farms can be open at once (not an accordion). Deliberately not shared with the Barn
    // expand/collapse state above: a Barn id and a Farm id are drawn from different id spaces, so
    // the same Set would risk a collision if a Barn and a Farm ever shared a numeric id.
    const { isExpanded: isChartExpanded, toggle: toggleChart, reset: resetChartExpanded } = useExpandedIds();

    // Switching animal type re-scopes the whole page to a different set of figures. A Barn/Farm id
    // left expanded/chart-open from the old type would keep rendering under the new one, which
    // reads as leftover state rather than a fresh view — Barn ids and Farm ids are stable across
    // types (same locations), so nothing here would raise an error, it would just look wrong. Both
    // Sets reset together since they're the whole of this page's "what's open" state.
    watch(isWeaners, (weaner) => {
        referenceWindowWeeks.value = readStoredReferenceWindow(weaner);
        resetExpanded();
        resetChartExpanded();
    });

    // A Barn row with no resolved target emits `setTarget` from its "Set" button — hand it off to
    // the modal's own openForFarm, which loads, scrolls to, and focuses that Farm's override input.
    const targetsModal = ref<InstanceType<typeof ManageObjectivesModal> | null>(null);
    function openTargetsFor(farmLocationId: number): void {
        targetsModal.value?.openForFarm(farmLocationId);
    }

    return {
        isWeaners,
        referenceWindowOptions,
        referenceWindowWeeks,
        selectReferenceWindow,
        isLoading,
        isError,
        refetch,
        isRefetchingPlaceholder,
        farmGroups,
        hasFarms,
        refetchAll,
        historyByFarmId,
        trendDirectionFor,
        leaderboard,
        leaderboardDisabledReason,
        barCeiling,
        isExpanded,
        toggle,
        isChartExpanded,
        toggleChart,
        targetsModal,
        openTargetsFor,
    };
}
